import React, { useContext, useState } from "react";
import {
  Card,
  Button,
  Input,
  Download,
  Store,
  Building2,
  Utensils,
  FolderOpen,
  Monitor,
  UserCog,
  StatusDropdown,
} from "../../../ui";
import { tx } from "../../ludConnect.constants";
import { fmtDateShort } from "../../helpers/ludConnect.helpers";
import "./RestaurantSummary.css";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { updateStoreAPI } from "../../../../utils/api/stores";

const Row = ({ label, children }) => (
  <div className="lud-connect__summary-row">
    <span>{label}</span>
    <b>{children}</b>
  </div>
);

const RestaurantSummary = ({
  ar,
  restaurant,
  menu,
  isProvider,
  onRestaurantFieldChange,
  onDownload,
  onError,
  linkOpenCount,
  editOpenCount,
  refresh,
}) => {
  const { handleTokenExpiration } = useContext(Context);
  const { can, has } = useAccess();
  // display only: the update handler below checks the restaurant itself
  const canEditStatus = has("ludConnect.restaurant.status.edit");
  // Only the clicked branch switches to a date input.
  const [editingDateId, setEditingDateId] = useState(null);
  const onUpdate = async (updatedBranches) => {
    // Execution-time check, bound to this restaurant's verified public id (NA-010 / NA-011).
    if (!can("ludConnect.restaurant.status.edit", { restaurantId: restaurant?.id })) {
      onError?.(
        new Error("not allowed"),
        tx("غير مصرّح لك بتنفيذ هذا الإجراء", "You are not allowed to do this", ar),
      );
      return;
    }
    const token = localStorage.getItem("auth-token");

    if (!token) {
      handleTokenExpiration();
      return;
    }

    try {
      const updatedStore = await updateStoreAPI(token, restaurant.mongoId, {
        branches: updatedBranches,
      });

      if (updatedStore) {
        await refresh?.();
      } else {
        throw new Error(
          tx("لم يتم حفظ حالة الفرع", "Branch status was not saved", ar),
        );
      }
    } catch (error) {
      onError?.(
        error,
        tx("تعذّر تحديث حالة الفرع", "Could not update the branch status", ar),
      );
    }
  };
  const totalItems = menu.sections.reduce(
    (sum, section) => sum + section.items.length,
    0,
  );
  return (
    <div className="lud-connect__summary-grid">
      <Card
        title={
          <span>
            <Store size={16} /> {tx("بيانات المطعم", "Restaurant data", ar)}
          </span>
        }
        hoverable={false}
      >
        <Row label={tx("الاسم العربي", "Arabic name", ar)}>
          {restaurant.nameAr || restaurant.name}
        </Row>
        <Row label={tx("الاسم الإنجليزي", "English name", ar)}>
          {restaurant.nameEn || "—"}
        </Row>
        <Row label={tx("المعرف", "ID", ar)}>{restaurant.id}</Row>
        <Row label={tx("المدينة", "City", ar)}>{restaurant.city}</Row>
        <Row label={tx("اسم التكامل", "Integration name", ar)}>
          {restaurant.integrationName || "—"}
        </Row>
        <Row label={tx("نوع الربط", "Connection type", ar)}>
          {restaurant.linkType}
        </Row>
        <Row label={tx("عدد الفروع", "Branches", ar)}>
          {restaurant.branches.length}
        </Row>
        <Row label={tx("طلبات ربط معلّقة", "Pending connection requests", ar)}>
          {linkOpenCount}
        </Row>
      </Card>
      <Card
        title={
          <span>
            <Building2 size={16} /> {tx("حالة الفروع", "Branch status", ar)}
          </span>
        }
        hoverable={false}
      >
        {restaurant.branches.map((branch, i) => {
          return (
            <Row key={branch.id} label={branch.name}>
            <span className="lud-connect__date-badge">
              {editingDateId === branch.id ? (
                <Input
                  type="date"
                  autoFocus
                  aria-label={tx(
                    `تاريخ تغيير حالة ${branch.name}`,
                    `${branch.name} status date`,
                    ar,
                  )}
                  value={
                    branch.statusChangedAt
                      ? new Date(branch.statusChangedAt)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const updated = [...restaurant.branches];
                    updated[i] = {
                      ...updated[i],
                      statusChangedAt: e.target.value
                        ? new Date(`${e.target.value}T00:00:00`)
                        : null,
                    };
                    onUpdate(updated);
                  }}
                  onBlur={() => setEditingDateId(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setEditingDateId(null);
                    }
                  }}
                />
              ) : canEditStatus ? (
                <button
                  type="button"
                  className="lud-connect__date-edit"
                  title={tx("تعديل التاريخ", "Edit date", ar)}
                  onClick={() => setEditingDateId(branch.id)}
                >
                  {fmtDateShort(branch.statusChangedAt, ar) ||
                    tx("إضافة تاريخ", "Add date", ar)}
                </button>
              ) : (
                <span>{fmtDateShort(branch.statusChangedAt, ar)}</span>
              )}

              <StatusDropdown
                value={branch.status}
                options={[
                  "new",
                  "active",
                  "closed",
                  "inactive",
                  "inProgress",
                  "maintenance",
                ]}
                editable={canEditStatus}
                getLabel={(value) =>
                  ({
                    new: tx("جديد", "New", ar),
                    inProgress: tx("قيد المعالجة", "In progress", ar),
                    active: tx("مفعّل", "Active", ar),
                    inactive: tx("غير مفعل", "Inactive", ar),
                    closed: tx("مغلق", "Closed", ar),
                    maintenance: tx("صيانة", "Maintenance", ar),
                  })[value] || value
                }
                onChange={(val) => {
                  const updated = [...restaurant.branches];

                  updated[i] = {
                    ...updated[i],
                    status: val,
                    // IMPORTANT: don't change statusChangedAt here
                  };
                  onUpdate(updated);
                }}
              />
            </span>
            </Row>
          );
        })}
      </Card>
      <Card
        title={
          <span>
            <Utensils size={16} /> {tx("المنيو", "Menu", ar)}
          </span>
        }
        hoverable={false}
      >
        <Row label={tx("الأقسام", "Sections", ar)}>{menu.sections.length}</Row>
        <Row label={tx("الأصناف", "Items", ar)}>{totalItems}</Row>
        <Row label={tx("طلبات تعديل معلّقة", "Pending update requests", ar)}>
          {editOpenCount}
        </Row>
      </Card>
      <Card
        title={
          <span>
            <FolderOpen size={16} /> {tx("المستندات", "Documents", ar)}
          </span>
        }
        hoverable={false}
      >
        {[
          {
            label: tx("الشعار", "Logo", ar),
            reference: "logo",
          },
          {
            label: tx("معلومات المنيو", "Menu information", ar),
            reference: "restaurantMenu",
          },
          {
            label: tx("صور المنتجات", "Product photos", ar),
            reference: "productPhotos",
          },
        ].map((item) => (
          <Row key={item.label} label={item.label}>
            <Button
              size="sm"
              variant="primary"
              appearance="filled"
              icon={<Download size={14} />}
              onClick={() => onDownload(item.reference)}
            >
              {tx("تحميل", "Download", ar)}
            </Button>
          </Row>
        ))}
      </Card>
      {restaurant.linkType === "نظام كاشير" && (
        <Card
          title={
            <span>
              <Monitor size={16} /> {tx("نظام الكاشير", "POS system", ar)}
            </span>
          }
          hoverable={false}
        >
          <div className="lud-connect__field-stack">
            {[
              [
                "name",
                tx(
                  "اسم النظام (مثال: Foodics)",
                  "System name (e.g. Foodics)",
                  ar,
                ),
              ],
              ["credId", tx("المعرف", "ID", ar)],
              ["email", tx("الإيميل", "Email", ar)],
              ["pass", tx("الرقم السري", "Password", ar)],
            ].map(([field, placeholder]) => (
              <Input
                key={field}
                value={restaurant.posSystem[field]}
                placeholder={placeholder}
                disabled={field === "name" || !isProvider}
                onChange={(event) =>
                  onRestaurantFieldChange(
                    "posSystem",
                    field,
                    event.target.value,
                  )
                }
              />
            ))}
          </div>
          {restaurant.posSystem.addedAt && (
            <div className="lud-connect__date-line">
              {tx("أُضيف", "Added", ar)}:{" "}
              {fmtDateShort(restaurant.posSystem.addedAt, ar)}
              {restaurant.posSystem.updatedAt && (
                <>
                  {" — "}
                  {tx("آخر تحديث", "Last update", ar)}:{" "}
                  {fmtDateShort(restaurant.posSystem.updatedAt, ar)}
                </>
              )}
            </div>
          )}
        </Card>
      )}
      <Card
        title={
          <span>
            <UserCog size={16} />{" "}
            {tx("حساب أدمن المطعم", "Restaurant admin account", ar)}
          </span>
        }
        hoverable={false}
      >
        <div className="lud-connect__field-stack">
          <Input
            value={restaurant.providerAdminEmail}
            placeholder={tx("الإيميل", "Email", ar)}
            onChange={(event) =>
              onRestaurantFieldChange(
                "provider",
                "providerAdminEmail",
                event.target.value,
              )
            }
          />
          <Input
            value={restaurant.providerAdminPass}
            placeholder={tx("الرقم السري", "Password", ar)}
            onChange={(event) =>
              onRestaurantFieldChange(
                "provider",
                "providerAdminPass",
                event.target.value,
              )
            }
          />
        </div>
      </Card>
    </div>
  );
};

export default RestaurantSummary;
